import Page from "./Page";
import { useDispatch, useSelector } from "react-redux";
import React, { useRef, useState } from "react";
import { setActiveTask } from "../redux/actions/tasks";
import { Container, Grid, Typography } from "@mui/material";
import Button from "../components/input/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import TaskDataContainer from "../components/tasks/TaskDataContainer";
import HelpLink from "../components/help/HelpLink";
import TaskUsage from "../components/account/TaskUsage";
import useCreateTask from "../hooks/tasks/useCreateTask";
import {
  validateTask,
  TASK_VALIDATION_LIMITS,
  parseBackendValidationErrors,
} from "../helpers/taskValidation";
import { isNetworkError } from "../helpers/networkErrors";

const defaultAttributes = {
  difficulty: 5,
  importance: 5,
  fun: 5,
};

const CreateTaskPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPage = searchParams.get("from");
  const { subscription } = useSelector((state) => state.user);
  const { createTask, saving } = useCreateTask();
  const noteInputValueRef = useRef();

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [notes, setNotes] = useState({});
  const [noteInputError, setNoteInputError] = useState("");
  const [targeted, setTargeted] = useState(false);
  const [labels, setLabels] = useState([]);
  const [emoji, setEmoji] = useState();
  const [attributes, setAttributes] = useState(defaultAttributes);
  const [hasDeadline, setHasDeadline] = useState();
  const [isRecurring, setRecurring] = useState(false);
  const [deadline, setDeadline] = useState();
  const [frequency, setFrequency] = useState();
  const [schedule, setSchedule] = useState();

  const [showEditLabelsPanel, setShowEditLabelsPanel] = useState(false);

  const onAttributeChange = (attribute) => {
    return (incrementer) => {
      const newAttributes = {
        ...attributes,
        [attribute]: attributes[attribute] + incrementer,
      };

      setAttributes(newAttributes);
    };
  };

  const onNameChange = (event) => {
    const newName = event.target.value;
    setName(newName);
    // Clear error if name is now valid, or show max length error
    if (newName.trim()) {
      if (newName.length > TASK_VALIDATION_LIMITS.NAME_MAX_LENGTH) {
        setNameError(
          `Task name must be ${TASK_VALIDATION_LIMITS.NAME_MAX_LENGTH} characters or less`,
        );
      } else {
        setNameError("");
      }
    }
  };

  const onChangeEditLabelPanelVisibility = (visibility) => {
    setShowEditLabelsPanel(visibility);
  };

  const onLabelsChange = (newLabels) => {
    setLabels(newLabels);
  };

  const onLabelRemoved = (labelId) => {
    const newLabels = [...labels];
    newLabels.splice(labels.indexOf(labelId), 1);

    setLabels(newLabels);
  };

  const onAddNote = (noteText) => {
    if (noteText !== "") {
      const newNotes = { ...notes };
      const newNoteId = +new Date();
      newNotes[newNoteId] = {
        text: noteText,
        id: newNoteId,
        completed: false,
      };

      setNotes(newNotes);
    }
  };

  const onNoteStatusChange = ({ id, status }) => {
    const newNotes = { ...notes };
    const newNote = newNotes[id];
    newNote.completed = status;

    setNotes(newNotes);
  };

  const onNoteDeleted = (id) => {
    const newNotes = { ...notes };
    delete newNotes[id];

    setNotes(newNotes);
  };

  const getReturnPath = () => {
    if (fromPage === "planner") {
      return "/planner";
    }
    if (fromPage === "dashboard") {
      return "/dashboard";
    }
    return "/tasks"; // default to tasks page
  };

  const onCancelClick = () => {
    navigate(getReturnPath());
    dispatch(setActiveTask(null));
  };

  const onAcceptClick = async () => {
    // Get unsaved note text for validation
    const unsavedNoteText = noteInputValueRef?.current?.getNoteValue() || "";

    const newTask = {
      name,
      notes,
      attributes,
      labels,
      targeted,
      deadline: deadline ? deadline.unix() : undefined,
      frequency,
      emoji,
      schedule: schedule ? schedule.unix() : undefined,
    };

    // Validate the task before submission
    const validation = validateTask(newTask, unsavedNoteText);

    if (!validation.isValid) {
      if (validation.errors.name) {
        setNameError(validation.errors.name);
      }
      if (validation.errors.unsavedNote) {
        setNoteInputError(validation.errors.unsavedNote);
      }
      return;
    }

    try {
      const success = await createTask(newTask, noteInputValueRef);
      if (success) {
        navigate(getReturnPath());
      }
    } catch (error) {
      // Handle validation errors from backend
      if (error.status === 400 && error.errors) {
        const parsedErrors = parseBackendValidationErrors(error.errors);
        if (parsedErrors.name) {
          setNameError(parsedErrors.name);
        }
        if (parsedErrors.unsavedNote || parsedErrors.notesCount) {
          setNoteInputError(
            parsedErrors.unsavedNote || parsedErrors.notesCount,
          );
        }
      } else if (isNetworkError(error)) {
        // Network/offline errors are already handled by useApiClient (shows offline dialog)
        // Just log and don't re-throw
        console.warn("Network error while creating task:", error.message);
      } else {
        // Re-throw for other error handling
        throw error;
      }
    }
  };

  return (
    <Page className="create-task-page">
      <Grid item xs={12}>
        <div className="create-task__header">
          <Typography
            variant="h2WhiteNoMargin"
            className="create-task__header-label"
          >
            Create Task
          </Typography>
          <HelpLink
            to="/about/features/tasks/create-a-task"
            color="white"
            size="large"
            sx={{ position: "absolute", right: 8 }}
          />
        </div>
      </Grid>
      <Container
        sx={{
          maxWidth: 800,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 0,
        }}
      >
        <TaskDataContainer
          name={name}
          onNameChange={onNameChange}
          nameError={!!nameError}
          nameHelperText={nameError}
          labels={labels}
          onLabelsChange={onLabelsChange}
          onLabelRemoved={onLabelRemoved}
          emoji={emoji}
          setEmoji={setEmoji}
          deadline={deadline}
          setHasDeadline={setHasDeadline}
          hasDeadline={hasDeadline}
          setDeadline={setDeadline}
          isRecurring={isRecurring}
          setRecurring={setRecurring}
          frequency={frequency}
          setFrequency={setFrequency}
          schedule={schedule}
          setSchedule={setSchedule}
          targeted={targeted}
          setTargeted={setTargeted}
          attributes={attributes}
          onAttributeChange={onAttributeChange}
          notes={notes}
          onAddNote={onAddNote}
          onNoteStatusChange={onNoteStatusChange}
          onNoteDeleted={onNoteDeleted}
          showEditLabelsPanel={showEditLabelsPanel}
          onChangeEditLabelPanelVisibility={onChangeEditLabelPanelVisibility}
          noteInputValueRef={noteInputValueRef}
          noteInputError={noteInputError}
          onNoteInputErrorChange={setNoteInputError}
        />
        {subscription?.plan === "free" && (
          <div className="create-task_usage-container">
            <TaskUsage />
          </div>
        )}

        {!showEditLabelsPanel && (
          <Grid item xs={12} md={6} className="create-task__footer">
            <Button
              text="Cancel"
              color="red"
              size="large"
              onClick={onCancelClick}
            />
            <Button
              text="Create"
              color="green"
              size="large"
              onClick={onAcceptClick}
              loading={saving}
            />
          </Grid>
        )}
      </Container>
    </Page>
  );
};

export default CreateTaskPage;
